// ─── Insight Topics ──────────────────────────────────────────────────────────

export const INSIGHT_TOPICS = [
  'claude-code', 'claude', 'openai', 'gemini', 'other-model',
  'ai-tools', 'training', 'research', 'datasets', 'news', 'general-ai',
] as const;

export type InsightTopic = (typeof INSIGHT_TOPICS)[number];

// ─── Insight Categories ─────────────────────────────────────────────────────

export const SEED_CATEGORIES = [
  'hook', 'claude-md-pattern', 'mcp-server',
  'tip', 'guide', 'workflow', 'tool', 'course', 'dataset', 'research',
  'news', 'prompt', 'configuration', 'integration', 'agent-pattern',
] as const;

export type InsightCategory = (typeof SEED_CATEGORIES)[number] | string;

// ─── Source Types ───────────────────────────────────────────────────────────

export const SOURCE_TYPES = [
  'x/twitter',
  'reddit',
  'bluesky',
  'mastodon',
  'github',
  'hackernews',
  'rss',
  'discord',
  'gmail',
  'telegram',
] as const;

export type SourceType = (typeof SOURCE_TYPES)[number];

// ─── Insight Status ─────────────────────────────────────────────────────────

export type InsightStatus = 'published' | 'review' | 'dismissed';

// ─── DynamoDB Entities ──────────────────────────────────────────────────────

export interface Insight {
  insightId: string; // ULID
  sk: 'META';
  title: string;
  summary: string;
  category: InsightCategory;
  topic: InsightTopic;
  tags: string[];
  relevanceScore: number; // 0-10
  status: InsightStatus;
  sourceUrl: string;
  sourceType: SourceType;
  sourceId: string;
  sourceAuthor: string;
  actionableSteps: string[];
  relatedInsightIds: string[];
  s3ContentKey: string;
  s3RawKey: string;
  enrichmentSources?: string[];
  telegramChatId?: number; // set when sourceType is 'telegram', avoids activity table scan on review
  bookmarked?: string; // "true" when bookmarked, absent when not (sparse GSI)
  userRating?: number; // 1-5 personal rating
  embedding?: string; // Base64-encoded float32[1024] from Titan Embeddings v2
  createdAt: string; // ISO 8601
  processedAt: string; // ISO 8601
  gsiAllPk?: string;
}

export interface Source {
  sourceId: string;
  sk: 'META';
  type: SourceType;
  name: string;
  identifier: string; // username, subreddit name, repo slug, RSS URL, etc.
  enabled: boolean;
  pollIntervalMinutes: number;
  lastPolledAt: string | null;
  lastError: string | null;
  consecutiveErrors?: number; // incremented on each poll failure, reset to 0 on success
  lastSuccessAt?: string | null; // ISO 8601 of last poll that produced no error
  filters: string[];
  config: Record<string, string>; // source-specific config (e.g., subreddit sort)
  createdAt: string;
  updatedAt: string;
}

// ─── Dynamic Categories ───────────────────────────────────────────────────────

export interface DynamoCategory {
  categoryId: string;
  sk: 'META';
  name: string;
  description: string;
  usageCount: number;
  createdAt: string;
  status: 'active';
}

// ─── AI Extraction ──────────────────────────────────────────────────────────

export interface ExtractionResult {
  relevanceScore: number;
  category: InsightCategory;
  topic: InsightTopic;
  title: string;
  summary: string;
  actionableSteps: string[];
  tags: string[];
  relatedTopics: string[];
}

// ─── SQS Message Payloads ───────────────────────────────────────────────────

export interface IngestMessage {
  messageType: 'url' | 'text' | 'email';
  sourceType: SourceType;
  sourceId: string;
  sourceUrl: string;
  sourceAuthor: string;
  rawContent: string;
  userNotes?: string;
  submittedAt: string;
  activityId?: string;
  telegramChatId?: number;
}

export type VideoPlatform = 'youtube' | 'tiktok' | 'instagram' | 'x-twitter' | 'generic';

export interface VideoProcessingMessage {
  ingestMessage: IngestMessage;
  videoUrl: string;
  platform: VideoPlatform;
  title: string;
  author: string;
  existingTranscript?: string;
  metadata: Record<string, string>;
  insightId: string;
}

// ─── API Request/Response Types ─────────────────────────────────────────────

export interface ListInsightsParams {
  category?: InsightCategory;
  topic?: InsightTopic;
  sourceType?: SourceType;
  minRelevance?: number;
  status?: InsightStatus;
  bookmarked?: boolean;
  sortBy?: 'createdAt' | 'relevanceScore' | 'title';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  cursor?: string; // DynamoDB pagination token (base64 encoded)
}

export interface ListInsightsResponse {
  items: Insight[];
  nextCursor: string | null;
  total: number;
}

export interface InsightDetailResponse {
  insight: Insight;
  markdownContent: string;
}

export interface ListSourcesResponse {
  items: Source[];
}

export interface CreateSourceRequest {
  type: SourceType;
  name: string;
  identifier: string;
  pollIntervalMinutes?: number;
  filters?: string[];
  config?: Record<string, string>;
}

export interface UpdateSourceRequest {
  name?: string;
  identifier?: string;
  enabled?: boolean;
  pollIntervalMinutes?: number;
  filters?: string[];
  config?: Record<string, string>;
}

export interface UpdateInsightRequest {
  bookmarked?: boolean;
  userRating?: number | null; // 1-5, or null to clear
}

export interface SearchRequest {
  query: string;
  category?: InsightCategory;
  minRelevance?: number;
  limit?: number;
}

export interface SearchResponse {
  items: Insight[];
  total: number;
}

export interface ReviewAction {
  action: 'accept' | 'dismiss';
}

export interface StatsResponse {
  totalInsights: number;
  totalSources: number;
  reviewQueueSize: number;
  byCategory: Record<string, number>;
  bySource: Record<string, number>;
  recentActivity: { date: string; count: number }[];
}

// ─── Telegram Activity ─────────────────────────────────────────────────────

export type TelegramProcessingStatus =
  | 'received'
  | 'queued'
  | 'processing'
  | 'published'
  | 'review'
  | 'discarded'
  | 'error'
  | 'command';

export interface TelegramActivity {
  activityId: string;
  sk: 'META';
  gsiPk: 'TELEGRAM';
  telegramMessageId: number;
  telegramChatId: number;
  senderName: string;
  messageText: string;
  messageDate: string;
  botReply: string;
  urls: string[];
  userNotes: string;
  messageType: 'url' | 'text' | 'command';
  processingStatus: TelegramProcessingStatus;
  insightIds: string[];
  relevanceScore: number | null;
  aiTitle: string | null;
  aiCategory: string | null;
  aiSummary: string | null;
  processingError: string | null;
  receivedAt: string;
  processedAt: string | null;
}

export interface ListActivityResponse {
  items: TelegramActivity[];
  nextCursor: string | null;
}

// ─── Telegram Webhook ───────────────────────────────────────────────────────

export interface TelegramUpdate {
  update_id: number;
  message?: {
    message_id: number;
    from: { id: number; first_name: string };
    chat: { id: number; type: string };
    date: number;
    text?: string;
    caption?: string;
    photo?: { file_id: string; file_unique_id: string; file_size: number; width: number; height: number }[];
    entities?: { type: string; offset: number; length: number; url?: string }[];
  };
}

// ─── Search Index ───────────────────────────────────────────────────────────

export interface SearchIndexEntry {
  id: string;
  title: string;
  summary: string;
  category: InsightCategory;
  tags: string[];
  relevanceScore: number;
  sourceType: SourceType;
  topic?: InsightTopic;
  createdAt: string;
}

// ─── Digest Archive ─────────────────────────────────────────────────────────

export interface DigestRecord {
  date: string;        // "YYYY-MM-DD"
  insightIds: string[]; // featured insight ULIDs (top 3)
  tipInsightId: string; // tip-of-the-day insight ULID
  sentAt: string;       // ISO 8601
}

// ─── Advisor ─────────────────────────────────────────────────────────────────

export interface AdvisorStep {
  number: number;
  title: string;
  description: string;
  insightId: string | null;
  insight?: Pick<Insight, 'insightId' | 'title' | 'category' | 'relevanceScore'>;
}

export interface AdvisorPlan {
  steps: AdvisorStep[];
  insightCount: number;
}
